import { CUBE_FACES } from './types'

/* ─── 离屏格式 / 滤波 ─── */

/** 离屏纹理格式: 无 EXT_color_buffer_float 时降级 RGBA8(不能渲染到 float 纹理) */
let _warnedNoFloat = false
export function pickFmt(gl: WebGL2RenderingContext, prefer: 'float32' | 'float16') {
  if (!gl.getExtension('EXT_color_buffer_float')) {
    if (!_warnedNoFloat) {
      _warnedNoFloat = true
      console.warn('EXT_color_buffer_float 不可用, 离屏 buffer 降级为 RGBA8: 自反馈累积类着色器可能出现色带')
    }
    return { internal: gl.RGBA8, type: gl.UNSIGNED_BYTE }
  }
  return prefer === 'float32'
    ? { internal: gl.RGBA32F, type: gl.FLOAT }
    : { internal: gl.RGBA16F, type: gl.HALF_FLOAT }
}

/** float 纹理 LINEAR 采样需专门扩展, 无则退回 NEAREST(避免采样返回 0) */
export function pickFilter(gl: WebGL2RenderingContext, type: number): number {
  if (type === gl.FLOAT) return gl.getExtension('OES_texture_float_linear') ? gl.LINEAR : gl.NEAREST
  if (type === gl.HALF_FLOAT) return gl.getExtension('OES_texture_half_float_linear') ? gl.LINEAR : gl.NEAREST
  return gl.LINEAR
}

/* ─── 噪点纹理 ─── */

// 256x256: 大量 shader 用 texelFetch(iChannel0, p&ivec2(255), 0) 依赖该尺寸
const TEX = 256

/**
 * 程序化噪点纹理(2D 或 cubemap): white=false 平滑 value noise(默认风格),
 * white=true 白噪点(hash 查表风格 noise 的格点随机源, 见 `// iChannelN: hashnoise`)。
 */
export function createNoiseTexture(gl: WebGL2RenderingContext, cube: boolean, white: boolean): WebGLTexture {
  const data = white ? makeWhiteNoise() : makeValueNoise()
  const tex = gl.createTexture()!
  if (cube) {
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, tex)
    for (const f of CUBE_FACES) gl.texImage2D(f, 0, gl.RGBA, TEX, TEX, 0, gl.RGBA, gl.UNSIGNED_BYTE, data)
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE)
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, null)
  } else {
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, TEX, TEX, 0, gl.RGBA, gl.UNSIGNED_BYTE, data)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
  }
  return tex
}

// 模块级缓存: value noise 确定性生成, 多实例共享复用(256×256×4 ≈ 256KB, 常驻可接受)
let _valueCache: Uint8Array | null = null

/**
 * TEX×TEX 平滑 value noise(确定性, 4 octave fbm)。
 * 连续值可安全用于高度场 ray march / 颜色源 / 通用噪声采样。
 */
function makeValueNoise(): Uint8Array {
  if (_valueCache) return _valueCache
  const hash = (i: number, j: number, o: number) => {
    const s = Math.sin(i * 127.1 + j * 311.7 + o * 74.7) * 43758.5453
    return s - Math.floor(s)
  }
  const d = new Uint8Array(TEX * TEX * 4)
  for (let y = 0; y < TEX; y++) {
    for (let x = 0; x < TEX; x++) {
      let v = 0, amp = 1, norm = 0
      for (let o = 0; o < 4; o++) {
        const G = 16 << o // 每 octave 格点密度翻倍(REPEAT 取模保证边缘连续)
        const gx = (x / TEX) * G, gy = (y / TEX) * G
        const ix = Math.floor(gx) % G, iy = Math.floor(gy) % G
        const u = gx - Math.floor(gx), vv = gy - Math.floor(gy)
        const su = u * u * (3 - 2 * u), sv = vv * vv * (3 - 2 * vv)
        const a = hash(ix, iy, o), b = hash(ix + 1, iy, o)
        const c = hash(ix, iy + 1, o), d2 = hash(ix + 1, iy + 1, o)
        v += amp * (a + (b - a) * su + (c - a) * sv + (a - b - c + d2) * su * sv)
        norm += amp
        amp *= 0.5
      }
      const val = ((v / norm) * 255) | 0
      const idx = (y * TEX + x) * 4
      d[idx] = d[idx + 1] = d[idx + 2] = val
      d[idx + 3] = 255
    }
  }
  _valueCache = d
  return d
}

// 模块级缓存: 白噪点首次生成后固定, 多实例共享复用
let _whiteCache: Uint8Array | null = null

/**
 * TEX×TEX 白噪点(每像素独立随机 RGBA)。
 * hash 风格 noise 要求每 texel 独立随机值, 不能用平滑纹理, 否则退化成模糊渐变。
 */
function makeWhiteNoise(): Uint8Array {
  if (_whiteCache) return _whiteCache
  const d = new Uint8Array(TEX * TEX * 4)
  for (let i = 0; i < d.length; i += 4) {
    d[i]     = (Math.random() * 256) | 0
    d[i + 1] = (Math.random() * 256) | 0
    d[i + 2] = (Math.random() * 256) | 0
    d[i + 3] = 255
  }
  _whiteCache = d
  return d
}